(() => {
  const namespace = window.LumiNovaBundleV5 || {};
  window.LumiNovaBundleV5 = namespace;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motionBehavior = () => reducedMotion() ? 'auto' : 'smooth';

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-ln-v5-gallery]');
    const items = Array.from(root.querySelectorAll('[data-ln-v5-gallery-item]'));
    const thumbs = Array.from(root.querySelectorAll('[data-ln-v5-gallery-thumb]'));
    if (!gallery || !items.length || !thumbs.length) return;

    const setActive = (index) => {
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        items[index]?.scrollIntoView({ behavior: motionBehavior(), block: 'nearest', inline: 'start' });
        setActive(index);
      });
    });

    let galleryFrame = null;
    gallery.addEventListener('scroll', () => {
      if (galleryFrame) window.cancelAnimationFrame(galleryFrame);
      galleryFrame = window.requestAnimationFrame(() => {
        let activeIndex = 0;
        let distance = Number.POSITIVE_INFINITY;
        items.forEach((item, index) => {
          const nextDistance = Math.abs(item.offsetLeft - gallery.scrollLeft);
          if (nextDistance < distance) {
            distance = nextDistance;
            activeIndex = index;
          }
        });
        setActive(activeIndex);
      });
    }, { passive: true });

    setActive(0);
  };

  const initMediaZoom = (root) => {
    const modal = root.querySelector('[data-ln-v5-media-modal]');
    const modalImage = modal?.querySelector('[data-ln-v5-media-modal-img]');
    const closeButton = modal?.querySelector('[data-ln-v5-media-close]');
    const openButtons = Array.from(root.querySelectorAll('[data-ln-v5-media-open]'));
    if (!modal || !modalImage || !closeButton || !openButtons.length || modal.dataset.lnV5MediaZoomReady === 'true') return;

    modal.dataset.lnV5MediaZoomReady = 'true';
    let lastTrigger = null;

    const closeModal = (restoreFocus = true) => {
      if (modal.hidden) return;
      modal.hidden = true;
      modalImage.removeAttribute('src');
      modal.scrollTop = 0;
      modal.scrollLeft = 0;
      document.documentElement.classList.remove('ln-bundle-v5-media-modal-open');
      if (restoreFocus && lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
    };

    const openModal = (button) => {
      const source = button.dataset.lnV5FullSrc;
      if (!source) return;
      lastTrigger = button;
      modalImage.src = source;
      modalImage.alt = button.dataset.lnV5MediaAlt || '';
      modal.hidden = false;
      modal.scrollTop = 0;
      modal.scrollLeft = 0;
      document.documentElement.classList.add('ln-bundle-v5-media-modal-open');
      closeButton.focus({ preventScroll: true });

      const positionMobileZoom = () => {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const maximumScroll = modal.scrollHeight - modal.clientHeight;
        modal.scrollTop = Math.max(0, maximumScroll * .18);
      };

      if (modalImage.complete) window.requestAnimationFrame(positionMobileZoom);
      else modalImage.addEventListener('load', positionMobileZoom, { once: true });
    };

    openButtons.forEach((button) => button.addEventListener('click', () => openModal(button)));
    closeButton.addEventListener('click', () => closeModal());
    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        closeButton.focus({ preventScroll: true });
      }
    });
    modalImage.addEventListener('contextmenu', (event) => event.preventDefault());
    modalImage.addEventListener('dragstart', (event) => event.preventDefault());
  };

  const initSliders = (root) => {
    root.querySelectorAll('[data-ln-v5-slider]').forEach((slider) => {
      if (slider.dataset.lnV5SliderReady === 'true') return;
      slider.dataset.lnV5SliderReady = 'true';

      const viewport = slider.querySelector('[data-ln-v5-slider-viewport]');
      const track = slider.querySelector('[data-ln-v5-slider-track]');
      const previous = slider.querySelector('[data-ln-v5-slider-prev]');
      const next = slider.querySelector('[data-ln-v5-slider-next]');
      if (!viewport || !track) return;

      const slides = Array.from(track.children);
      const step = () => {
        const firstSlide = slides[0];
        if (!firstSlide) return viewport.clientWidth;
        const style = window.getComputedStyle(track);
        const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
        return firstSlide.getBoundingClientRect().width + gap;
      };

      const update = () => {
        const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        if (previous) previous.disabled = viewport.scrollLeft <= 2;
        if (next) next.disabled = viewport.scrollLeft >= maximum - 2;
      };

      previous?.addEventListener('click', () => viewport.scrollBy({ left: -step(), behavior: motionBehavior() }));
      next?.addEventListener('click', () => viewport.scrollBy({ left: step(), behavior: motionBehavior() }));
      viewport.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const initTabs = (root) => {
    root.querySelectorAll('[data-ln-v5-tabs]').forEach((tabsRoot) => {
      if (tabsRoot.dataset.lnV5TabsReady === 'true') return;
      tabsRoot.dataset.lnV5TabsReady = 'true';

      const tabs = Array.from(tabsRoot.querySelectorAll('[role="tab"]'));
      const panels = Array.from(tabsRoot.querySelectorAll('[role="tabpanel"]'));
      if (!tabs.length) return;

      const activate = (nextTab, moveFocus = false) => {
        tabs.forEach((tab) => {
          const active = tab === nextTab;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          panel.hidden = panel.id !== nextTab.getAttribute('aria-controls');
        });
        if (moveFocus) nextTab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          activate(tabs[nextIndex], true);
        });
      });

      activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    });
  };

  const initForms = (root) => {
    root.querySelectorAll('[data-ln-v5-form-config]').forEach((configNode) => {
      if (configNode.dataset.lnV5ConfigReady === 'true') return;
      configNode.dataset.lnV5ConfigReady = 'true';
      let config = {};
      try {
        config = JSON.parse(configNode.textContent || '{}');
      } catch (error) {
        return;
      }
      const form = document.getElementById(config.formId);
      if (!form) return;
      form.setAttribute('data-ln-v5-form', '');
      form.dataset.lnV5ProductKey = String(config.productKey || '');
      form.dataset.lnV5Available = String(Boolean(config.available));
      form.dataset.lnV5ButtonLabel = config.buttonLabel || '';
      form.dataset.lnV5ShowPrice = String(Boolean(config.showPrice));
      form.dataset.lnV5DefaultLabel = config.defaultLabel || '';
      form.dataset.lnV5SoldOutLabel = config.soldOutLabel || '';
      form.dataset.lnV5AddingLabel = config.addingLabel || '';
      form.dataset.lnV5AddedLabel = config.addedLabel || '';
      form.dataset.lnV5ErrorLabel = config.errorLabel || '';
    });
  };

  const initVariantSelector = (root) => {
    const optionsRoot = root.querySelector('[data-ln-single-v2-options]');
    const dataNode = root.querySelector('[data-ln-single-v2-variant-data]');
    if (!optionsRoot || !dataNode || optionsRoot.dataset.lnSingleV2OptionsReady === 'true') return;

    let config = {};
    try {
      config = JSON.parse(dataNode.textContent || '{}');
    } catch (error) {
      return;
    }

    const productKey = String(config.productKey || '');
    const variants = Array.isArray(config.variants) ? config.variants : [];
    const selects = Array.from(optionsRoot.querySelectorAll('[data-ln-single-v2-option-select]'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
    if (!productKey || !variants.length || !selects.length) return;

    optionsRoot.dataset.lnSingleV2OptionsReady = 'true';

    const findVariant = () => variants.find((variant) => (
      Array.isArray(variant.options)
      && variant.options.length === selects.length
      && variant.options.every((value, index) => String(value) === selects[index].value)
    ));

    const updateVariant = (variant) => {
      const available = Boolean(variant?.available);
      const validPrice = variant?.validPriceMoney || '';
      const showOldPrice = Boolean(variant?.showOldPrice);

      root.querySelectorAll(`[data-ln-v5-form][data-ln-v5-product-key="${CSS.escape(productKey)}"]`).forEach((form) => {
        const variantInput = form.querySelector('[name="id"]');
        const button = form.querySelector('[data-ln-v5-atc]');
        const label = button?.querySelector('[data-ln-v5-atc-label]');
        const error = form.querySelector('[data-ln-v5-atc-error]');
        if (variantInput && variant?.id) variantInput.value = String(variant.id);

        form.dataset.lnV5Available = String(available);
        const buttonLabel = form.dataset.lnV5ButtonLabel || form.dataset.lnV5DefaultLabel;
        form.dataset.lnV5DefaultLabel = form.dataset.lnV5ShowPrice === 'true' && validPrice
          ? `${buttonLabel} — ${validPrice}`
          : buttonLabel;

        if (button) {
          button.disabled = !available;
          button.classList.remove('is-loading');
          button.setAttribute('aria-busy', 'false');
        }
        if (label) label.textContent = available ? form.dataset.lnV5DefaultLabel : form.dataset.lnV5SoldOutLabel;
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
      });

      root.querySelectorAll('[data-ln-single-v2-valid-price]').forEach((price) => {
        if (validPrice) price.textContent = validPrice;
      });
      root.querySelectorAll('[data-ln-single-v2-old-price]').forEach((price) => {
        price.hidden = !showOldPrice;
        if (variant?.oldPriceMoney) price.textContent = variant.oldPriceMoney;
      });
      root.querySelectorAll('[data-ln-single-v2-old-price-separator]').forEach((separator) => {
        separator.hidden = !showOldPrice || !available;
      });
      root.querySelectorAll('[data-ln-single-v2-stock]').forEach((stock) => {
        stock.hidden = !available;
      });

      if (variant?.id && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url);
      }

      root.dispatchEvent(new CustomEvent('luminova:variant:change', {
        bubbles: true,
        detail: { variant }
      }));
    };

    selects.forEach((select) => {
      select.addEventListener('change', () => updateVariant(findVariant()));
    });
  };

  namespace.stickyUpdates = namespace.stickyUpdates || new Set();

  const initSticky = (root) => {
    const stickyBars = Array.from(root.querySelectorAll('[data-ln-v5-sticky]'));
    const mainButton = root.querySelector('[data-ln-v5-main-atc]');
    if (!stickyBars.length || !mainButton) return;
    const usePdpV3Visibility = root.dataset.lnV5StickyVisibility === 'pdp-v3';

    const getThemeFooter = () => {
      if (!usePdpV3Visibility) {
        return document.querySelector('footer, .shopify-section-group-footer-group');
      }

      const footerCandidates = Array.from(document.querySelectorAll(
        '.shopify-section-group-footer-group, #shopify-section-footer, body > footer'
      ));

      return footerCandidates.find((candidate) => {
        const candidateStyle = window.getComputedStyle(candidate);
        const candidateRect = candidate.getBoundingClientRect();
        return candidateStyle.display !== 'none'
          && candidateStyle.visibility !== 'hidden'
          && candidateRect.width > 0
          && candidateRect.height > 0;
      }) || null;
    };

    const update = () => {
      if (!root.isConnected) {
        namespace.stickyUpdates.delete(update);
        return;
      }
      const mobileViewport = window.matchMedia('(max-width: 749px)').matches;
      const mainRect = mainButton.getBoundingClientRect();
      const finalSection = usePdpV3Visibility ? null : document.querySelector('[data-ln-v5-final]');
      const footer = getThemeFooter();
      const finalRect = finalSection?.getBoundingClientRect();
      const finalVisible = finalRect ? finalRect.top <= window.innerHeight && finalRect.bottom >= 0 : false;
      const largestStickyHeight = stickyBars.reduce((height, sticky) => Math.max(height, sticky.offsetHeight), 0) || 96;
      const footerOffset = usePdpV3Visibility ? 28 : 24;
      const footerNear = footer ? footer.getBoundingClientRect().top < window.innerHeight + largestStickyHeight + footerOffset : false;
      const documentBottomNear = usePdpV3Visibility
        ? window.innerHeight + window.scrollY > document.documentElement.scrollHeight - largestStickyHeight - 90
        : false;
      const mainPassed = mainRect.bottom < 0;

      stickyBars.forEach((sticky) => {
        const mode = sticky.dataset.lnV5StickyMode || 'mobile';
        const viewportMatches = mode === 'desktop' ? !mobileViewport : mobileViewport;
        sticky.classList.toggle('is-visible', viewportMatches && mainPassed && !finalVisible && !footerNear && !documentBottomNear);
      });
    };

    namespace.stickyUpdates.add(update);
    update();
  };

  namespace.init = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-ln-v5-root]')) roots.push(scope);
    scope.querySelectorAll?.('[data-ln-v5-root]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root.dataset.lnV5Ready === 'true') return;
      root.dataset.lnV5Ready = 'true';
      initForms(root);
      initVariantSelector(root);
      initGallery(root);
      initMediaZoom(root);
      initSliders(root);
      initTabs(root);
      initSticky(root);
    });
  };

  if (!namespace.booted) {
    namespace.booted = true;
    namespace.productLocks = new Set();

    const setProductState = (productKey, state, message = '') => {
      document.querySelectorAll(`[data-ln-v5-form][data-ln-v5-product-key="${CSS.escape(productKey)}"]`).forEach((form) => {
        const button = form.querySelector('[data-ln-v5-atc]');
        const label = button?.querySelector('[data-ln-v5-atc-label]');
        const error = form.querySelector('[data-ln-v5-atc-error]');
        if (!button || !label) return;

        const unavailable = form.dataset.lnV5Available !== 'true';
        button.disabled = unavailable || state === 'loading';
        button.classList.toggle('is-loading', state === 'loading');
        button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

        if (state === 'loading') label.textContent = form.dataset.lnV5AddingLabel;
        else if (state === 'added') label.textContent = form.dataset.lnV5AddedLabel;
        else if (state === 'error') label.textContent = form.dataset.lnV5ErrorLabel;
        else label.textContent = unavailable ? form.dataset.lnV5SoldOutLabel : form.dataset.lnV5DefaultLabel;

        if (error) {
          error.hidden = state !== 'error';
          error.textContent = state === 'error' ? message : '';
        }
      });
    };

    const updateCartCount = async () => {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        bubble.classList.toggle('hidden', cart.item_count === 0);
        const visible = bubble.querySelector('[aria-hidden="true"]');
        const hidden = bubble.querySelector('.visually-hidden');
        if (visible) visible.textContent = cart.item_count;
        if (hidden) hidden.textContent = `${cart.item_count} items`;
      });
    };

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest?.('[data-ln-v5-form]');
      if (!form) return;
      event.preventDefault();

      const productKey = form.dataset.lnV5ProductKey;
      const submitter = event.submitter || form.querySelector('[data-ln-v5-atc]');
      if (!productKey || namespace.productLocks.has(productKey) || form.dataset.lnV5Available !== 'true') return;

      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput?.value) return;

      namespace.productLocks.add(productKey);
      setProductState(productKey, 'loading');

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');
        const sections = [];

        if (typeof cartDrawer?.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((section) => sections.push(section.id));
        } else if (typeof cartNotification?.getSectionsToRender === 'function') {
          cartNotification.getSectionsToRender().forEach((section) => sections.push(section.id));
        } else {
          sections.push('cart-drawer', 'cart-icon-bubble');
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{ id: Number(variantInput.value), quantity: 1 }],
            sections: sections.join(','),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || form.dataset.lnV5ErrorLabel);

        if (typeof cartDrawer?.renderContents === 'function') {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.setActiveElement?.(submitter);
          cartDrawer.renderContents(data);
        } else if (typeof cartNotification?.renderContents === 'function') {
          cartNotification.setActiveElement?.(submitter);
          cartNotification.renderContents(data);
        } else {
          await updateCartCount().catch(() => {});
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        setProductState(productKey, 'added');
        window.setTimeout(() => setProductState(productKey, 'default'), 1600);
      } catch (error) {
        setProductState(productKey, 'error', error.message || form.dataset.lnV5ErrorLabel);
        window.setTimeout(() => setProductState(productKey, 'default'), 2200);
      } finally {
        namespace.productLocks.delete(productKey);
      }
    });

    const updateStickyBars = () => namespace.stickyUpdates.forEach((update) => update());
    window.addEventListener('scroll', updateStickyBars, { passive: true });
    window.addEventListener('resize', updateStickyBars);
    document.addEventListener('shopify:section:load', (event) => namespace.init(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.querySelector?.('[data-ln-v5-media-modal]:not([hidden])')) {
        document.documentElement.classList.remove('ln-bundle-v5-media-modal-open');
      }
      updateStickyBars();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => namespace.init(document), { once: true });
  } else {
    namespace.init(document);
  }
})();
